import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Chip,
  Tooltip,
  Paper,
  Avatar,
  Stack,
  LinearProgress,
  IconButton,
  alpha,
  Snackbar,
  Alert,
  Icon,
  Backdrop,
} from "@mui/material";
import Lottie from "react-lottie";
import * as animationData from "./../../assets/loader/loader.json";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import StatsCard from "components/Status";
import { useNavigate, useLocation } from "react-router-dom";
import { useRef } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

// Icons
import EventIcon from "@mui/icons-material/Event";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const salaryRef = useRef(null);
  const [greeting, setGreeting] = useState("");
  const [greetingIcon, setGreetingIcon] = useState("☀️");

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  // Date range filters
  // Section 1: Attendance (Range)
  const [attendanceFromDate, setAttendanceFromDate] = useState(dayjs().startOf("month"));
  const [attendanceToDate, setAttendanceToDate] = useState(dayjs().endOf("month"));
  // Internal UI states to prevent premature refetch
  const [tempAttendanceFromDate, setTempAttendanceFromDate] = useState(dayjs().startOf("month"));
  const [tempAttendanceToDate, setTempAttendanceToDate] = useState(dayjs().endOf("month"));

  // Section 2: Reports (Single Month)
  const [reportsDate, setReportsDate] = useState(dayjs());
  const [tempReportsDate, setTempReportsDate] = useState(dayjs());

  // Salary Update state
  const [salaryDate, setSalaryDate] = useState(dayjs());
  const [salaryStatus, setSalaryStatus] = useState("");
  const [initialSalaryStatus, setInitialSalaryStatus] = useState("");
  const [isSubmittingSalary, setIsSubmittingSalary] = useState(false);
  const [salarySnackbar, setSalarySnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleCloseSalarySnackbar = () => setSalarySnackbar({ ...salarySnackbar, open: false });

  // Salary Date limits
  const salaryMinDateLimit = dayjs().subtract(1, 'year').startOf('year');
  const salaryMaxDateLimit = dayjs().add(1, 'year').endOf('year');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting("Good Morning");
      setGreetingIcon("🌅");
    } else if (hour < 17) {
      setGreeting("Good Afternoon");
      setGreetingIcon("☀️");
    } else {
      setGreeting("Good Evening");
      setGreetingIcon("🌆");
    }
  }, []);

  // Generic fetch function
  const fetchDashboardData = async (start, end) => {
    const params = {
      fromDate: start.format("YYYY-MM"),
      toDate: end.format("YYYY-MM"),
    };

    const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/user-dashboard`, {
      params,
      withCredentials: true,
    });
    return res.data.data;
  };

  const {
    data: reportsData,
    isLoading: isReportsLoading,
    isFetching: isFetchingReports,
    refetch: refetchReports,
  } = useQuery({
    queryKey: ["userDashboardReports", reportsDate.format("YYYY-MM")],
    queryFn: () => fetchDashboardData(reportsDate.startOf("month"), reportsDate.endOf("month")),
  });

  const {
    data: attendanceData,
    isLoading: isAttendanceLoading,
    isFetching: isFetchingAttendance,
    refetch: refetchAttendance,
  } = useQuery({
    queryKey: ["userDashboardAttendance", attendanceFromDate.format("YYYY-MM"), attendanceToDate.format("YYYY-MM")],
    queryFn: () => fetchDashboardData(attendanceFromDate, attendanceToDate),
  });

  const isLoading = isAttendanceLoading || isReportsLoading;
  const isFetching = isFetchingAttendance || isFetchingReports;

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
  });

  useEffect(() => {
    // Check salary info from attendanceData primarily, or reportsData if needed (though usually salary is global or monthly)
    // Assuming salary info comes in both or we prioritize one. API likely returns same structure.
    // Let's use attendanceData for salary status as it uses the range picker which might be more relevant for "Month" context if users think of it that way,
    // BUT the salary update section has its own date picker.
    // However, for the initial status check, we can look at the latest loaded data.
    const data = attendanceData || reportsData;
    if (data) {
      // Prioritize salary_status then salary, default to "Not Received" if both empty
      const status = data.salary_status || data.salary || "Not Received";
      setSalaryStatus(status);
      setInitialSalaryStatus(status);
    }
  }, [attendanceData, reportsData]);

  useEffect(() => {
    if (!isLoading && location.state?.scrollToSalary && salaryRef.current) {
      const timer = setTimeout(() => {
        salaryRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        // Clear state to avoid scrolling again on next mount/render
        navigate(location.pathname, { replace: true, state: {} });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, location.state, navigate, location.pathname]);

  const handleSubmitSalary = async () => {
    if (!salaryStatus) {
      setSalarySnackbar({ open: true, message: "Please select salary status.", severity: "warning" });
      return;
    }

    try {
      setIsSubmittingSalary(true);
      const payload = {
        user_id: meData?.user_id,
        district: meData?.district,
        month: salaryDate.format("YYYY-MM"),
        status: salaryStatus,
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/salary/update`, payload, {
        withCredentials: true,
      });

      setInitialSalaryStatus(salaryStatus);
      setSalarySnackbar({
        open: true,
        message: response.data.message || "Salary status updated successfully!",
        severity: "success",
      });
    } catch (error) {
      setSalarySnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update salary status.",
        severity: "error",
      });
    } finally {
      setIsSubmittingSalary(false);
    }
  };

  // Authorization check
  if (meData && meData.role_id !== 3) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="60vh"
          flexDirection="column"
        >
          <Box textAlign="center">
            <Typography
              variant="h1"
              sx={{ fontWeight: 700, mb: 2, color: "#6B7280", fontSize: "120px" }}
            >
              403
            </Typography>
            <Typography variant="h5" sx={{ mb: 1, color: "#374151", fontWeight: 600 }}>
              Access Denied
            </Typography>
            <Typography variant="body1" sx={{ color: "#6B7280", mb: 3 }}>
              This dashboard is only accessible to Green Fellows.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/")}
              sx={{
                bgcolor: "#6366F1",
                textTransform: "none",
                px: 4,
                py: 1.5,
                borderRadius: "12px",
                fontWeight: 600,
                "&:hover": { bgcolor: "#4F46E5" },
              }}
            >
              Go to Home
            </Button>
          </Box>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!meData) return null;

  // Calculate completion percentage (using combined data)
  // We can merge data for completion calculation
  const mergedData = { ...attendanceData, ...reportsData };
  const totalTasks = 8;
  const completedTasks = [
    mergedData?.submissions?.total_submissions > 0,
    mergedData?.attendance?.present_count > 0,

    (Array.isArray(mergedData?.claims) && mergedData.claims.length > 0),
    mergedData?.mission_tracker?.submitted_weeks?.length > 0,
    mergedData?.weekly_reports?.submitted_weeks?.length > 0,
    mergedData?.tickets?.open_tickets === 0,
    mergedData?.lastActive,
    true,
  ].filter(Boolean).length;
  const completionPercentage = Math.round((completedTasks / totalTasks) * 100);

  // Normalize lessons to array
  const lessons = Array.isArray(mergedData?.lessons)
    ? mergedData.lessons
    : (mergedData?.lessons ? [mergedData.lessons] : []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        sx={{
          background: "linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)",
          minHeight: "100vh",
          py: 4,
          px: { xs: 2, md: 4 },
        }}
      >

        {/* Section 1: Attendance Overview */}
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "20px",
              border: "1px solid #E5E7EB",
              bgcolor: "white",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  bgcolor: "#EEF2FF",
                  display: "inline-flex",
                }}
              >
                <CalendarMonthIcon sx={{ color: "#6366F1", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
                  Attendance Overview
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  View attendance statistics
                </Typography>
              </Box>
            </Stack>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label="From Month"
                    views={["year", "month"]}
                    value={tempAttendanceFromDate}
                    onChange={(newValue) => setTempAttendanceFromDate(newValue)}
                    onAccept={(newValue) => setAttendanceFromDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#6366F1" },
                            "&.Mui-focused fieldset": { borderColor: "#6366F1" },
                          },
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label="To Month"
                    views={["year", "month"]}
                    value={tempAttendanceToDate}
                    onChange={(newValue) => setTempAttendanceToDate(newValue)}
                    onAccept={(newValue) => setAttendanceToDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#6366F1" },
                            "&.Mui-focused fieldset": { borderColor: "#6366F1" },
                          },
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            <MDBox mt={3}>
              <Grid container spacing={2.5}>
                {/* Days Present */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(16, 185, 129, 0.15)",
                        borderColor: "#10B981",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#D1FAE5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleIcon sx={{ color: "#10B981", fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 800, color: "#111827", fontSize: "2rem" }}
                        >
                          {attendanceData?.attendance?.present_count || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500, mt: 0.5 }}>
                          Days Present
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Leave Approved */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(251, 146, 60, 0.15)",
                        borderColor: "#FB923C",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#FFF7ED",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CalendarMonthIcon sx={{ color: "#FB923C", fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 800, color: "#111827", fontSize: "2rem" }}
                        >
                          {attendanceData?.attendance?.leave_count || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500, mt: 0.5 }}>
                          Leave Approved
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Days Absent */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(239, 68, 68, 0.15)",
                        borderColor: "#EF4444",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#FEE2E2",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon sx={{ color: "#EF4444", fontSize: 28 }}>person_off</Icon>
                      </Box>
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 800, color: "#111827", fontSize: "2rem" }}
                        >
                          {attendanceData?.attendance?.absent_count || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500, mt: 0.5 }}>
                          Days Absent
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Last Login */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(168, 85, 247, 0.15)",
                        borderColor: "#A855F7",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#FAF5FF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon sx={{ color: "#A855F7", fontSize: 28 }}>schedule</Icon>
                      </Box>
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 800, color: "#111827", fontSize: "1.5rem" }}
                        >
                          {attendanceData?.todayCheckIn
                            ? dayjs(attendanceData.todayCheckIn, "HH:mm:ss").format("hh:mm A")
                            : "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500, mt: 0.5 }}>
                          {attendanceData?.todayCheckIn ? "Checked in today" : "No attendance today"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500, mt: 0.5, display: "block" }}>
                          Check-in Time
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </MDBox>
          </Paper>
        </Box>

        {/* Section 2: Reports & Claims */}
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "20px",
              border: "1px solid #E5E7EB",
              bgcolor: "white",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  bgcolor: "#FCE7F3",
                  display: "inline-flex",
                }}
              >
                <Icon sx={{ color: "#EC4899", fontSize: 24 }}>summarize</Icon>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
                  Reports & Claims
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  Track submissions and claims
                </Typography>
              </Box>
            </Stack>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label="Select Month"
                    views={["year", "month"]}
                    value={tempReportsDate}
                    onChange={(newValue) => setTempReportsDate(newValue)}
                    onAccept={(newValue) => setReportsDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#EC4899" },
                            "&.Mui-focused fieldset": { borderColor: "#EC4899" },
                          },
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            <MDBox mt={3}>
              <Grid container spacing={2.5}>
                {/* Mission Tracker */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(99, 102, 241, 0.15)",
                        borderColor: "#6366F1",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#EEF2FF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon sx={{ color: "#6366F1", fontSize: 28 }}>track_changes</Icon>
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                          <Tooltip
                            title={reportsData?.mission_tracker?.submitted_weeks?.includes("monthly")
                              ? "✓ Monthly Progress Submitted"
                              : "✗ Not Submitted"
                            }
                            arrow
                          >
                            <Box
                              sx={{
                                px: 2,
                                height: 32,
                                borderRadius: "8px",
                                bgcolor: reportsData?.mission_tracker?.submitted_weeks?.includes("monthly")
                                  ? "#D1FAE5"
                                  : "#FEE2E2",
                                border: `2px solid ${reportsData?.mission_tracker?.submitted_weeks?.includes("monthly")
                                  ? "#10B981"
                                  : "#EF4444"
                                  }`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: reportsData?.mission_tracker?.submitted_weeks?.includes("monthly")
                                  ? "#10B981"
                                  : "#EF4444",
                                fontSize: "12px",
                                fontWeight: "bold",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": {
                                  transform: "scale(1.05)",
                                },
                              }}
                            >
                              MONTHLY
                            </Box>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500 }}>
                          Mission Tracker
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Weekly Reports */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(236, 72, 153, 0.15)",
                        borderColor: "#EC4899",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#FCE7F3",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon sx={{ color: "#EC4899", fontSize: 28 }}>summarize</Icon>
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                          {[1, 2, 3, 4].map((week) => (
                            <Tooltip
                              key={week}
                              title={`Week ${week}: ${reportsData?.weekly_reports?.submitted_weeks?.includes(week)
                                ? "✓ Submitted"
                                : "✗ Not Submitted"
                                }`}
                              arrow
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "8px",
                                  bgcolor: reportsData?.weekly_reports?.submitted_weeks?.includes(week)
                                    ? "#D1FAE5"
                                    : "#FEE2E2",
                                  border: `2px solid ${reportsData?.weekly_reports?.submitted_weeks?.includes(week)
                                    ? "#10B981"
                                    : "#EF4444"
                                    }`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: reportsData?.weekly_reports?.submitted_weeks?.includes(week)
                                    ? "#10B981"
                                    : "#EF4444",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    transform: "scale(1.1)",
                                  },
                                }}
                              >
                                {week}
                              </Box>
                            </Tooltip>
                          ))}
                        </Stack>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500 }}>
                          Weekly Reports
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Claims Status */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 12px 40px ${Array.isArray(reportsData?.claims) && reportsData?.claims.length > 0 ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)"
                          }`,
                        borderColor: Array.isArray(reportsData?.claims) && reportsData?.claims.length > 0 ? "#10B981" : "#EF4444",
                      },
                    }}
                    onClick={() => {
                      const claims = reportsData?.claims;
                      const hasClaimRecord = Array.isArray(claims) && claims.length > 0;

                      if (hasClaimRecord) {
                        const claim = claims[0];
                        const subFields = [claim.attendance_sheet, claim.stipend_claim, claim.travel_bills];
                        const submittedCount = subFields.filter((f) => f === "Submitted").length;

                        if (submittedCount === 3) {
                          // Fully submitted -> Go to list with filter
                          const monthDate = dayjs(claim.month);
                          navigate("/claims/list", {
                            state: {
                              month: monthDate.month() + 1,
                              year: monthDate.year(),
                            },
                          });
                        } else if (submittedCount === 0) {
                          // Fully not submitted -> Go to add with month pre-selected
                          navigate("/claims/add", {
                            state: { month: claim.month },
                          });
                        } else {
                          // Partial submission -> Go to edit
                          navigate(`/claims/edit/${claim.id}/${claim.user_id}`);
                        }
                      } else {
                        // No record for this month -> Go to add with current filter month
                        navigate("/claims/add", {
                          state: { month: reportsDate.format("YYYY-MM") },
                        });
                      }
                    }}
                  >
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "16px",
                            bgcolor: Array.isArray(reportsData?.claims) && reportsData?.claims.length > 0 ? "#D1FAE5" : "#FEE2E2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon
                            sx={{
                              color: Array.isArray(reportsData?.claims) && reportsData?.claims.length > 0 ? "#10B981" : "#EF4444",
                              fontSize: 28,
                            }}
                          >
                            receipt_long
                          </Icon>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="#111827">
                            Claims Status
                          </Typography>
                          <Typography variant="caption" color="text">
                            {reportsData?.claims?.[0]?.month ? dayjs(reportsData.claims[0].month).format('MMMM YYYY') : "Current Month"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 1 }}>
                        {Array.isArray(reportsData?.claims) && reportsData?.claims.length > 0 ? (
                          <Stack spacing={1}>
                            {[
                              { label: "Attendance", key: "attendance_sheet" },
                              { label: "Stipend", key: "stipend_claim" },
                              { label: "Travel Bills", key: "travel_bills" }
                            ].map((item) => {
                              const status = reportsData.claims[0][item.key];
                              const isSubmitted = status === "Submitted";
                              return (
                                <Box key={item.key} display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                  <Typography variant="body2" color="text" sx={{ fontSize: '0.875rem' }}>{item.label}</Typography>
                                  <Chip
                                    label={isSubmitted ? "Submitted" : "Not Submitted"}
                                    size="small"
                                    sx={{
                                      height: '24px',
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      bgcolor: isSubmitted ? "#D1FAE5" : "#FEE2E2",
                                      color: isSubmitted ? "#10B981" : "#EF4444",
                                      border: "none",
                                      "& .MuiChip-label": { px: 1 }
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Box display="flex" flexDirection="column" alignItems="center" py={1}>
                            <Typography variant="body2" color="text" align="center" fontStyle="italic">
                              No claims data found for this month.
                            </Typography>
                            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                              Click to Add
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Open Tickets */}
                <Grid item xs={6} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: "1px solid #E5E7EB",
                      bgcolor: "white",
                      height: "100%",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(59, 130, 246, 0.15)",
                        borderColor: "#3B82F6",
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "16px",
                          bgcolor: "#DBEAFE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SupportAgentIcon sx={{ color: "#3B82F6", fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 800, color: "#111827", fontSize: "2rem" }}
                        >
                          {reportsData?.tickets?.open_tickets || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500, mt: 0.5 }}>
                          Open Tickets
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </MDBox>
          </Paper>
        </Box>

        {/* Content Section - Lessons & Events removed to move Lessons below Salary Update */}

        {/* Salary Update Section */}
        {/* Split Row Section: Salary Update & Lessons from the field */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Salary Update Column */}
          <Grid item xs={12} lg={7}>
            <Paper
              ref={salaryRef}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "20px",
                border: "1px solid #E5E7EB",
                bgcolor: "white",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: "#FEF3C7",
                    display: "inline-flex",
                  }}
                >
                  <TrendingUpIcon sx={{ color: "#F59E0B", fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
                    Salary Update
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Update your monthly salary status
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={3} sx={{ flexGrow: 1 }}>
                <Box>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: "block", color: "#374151" }}>
                    Select Month
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Month and Year"
                      views={["year", "month"]}
                      value={salaryDate}
                      onChange={(newValue) => setSalaryDate(newValue)}
                      minDate={salaryMinDateLimit}
                      maxDate={salaryMaxDateLimit}
                      shouldDisableYear={(year) => year.isAfter(dayjs(), "year")}
                      shouldDisableMonth={(month) => month.isAfter(dayjs(), "month")}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          InputProps: {
                            readOnly: true,
                            sx: { fontSize: "14px", height: "45px" },
                          },
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                <Box>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: "block", color: "#374151" }}>
                    Salary Status
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ height: "45px", alignItems: "center" }}>
                    <Chip
                      label="Received"
                      onClick={() => setSalaryStatus("Received")}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 600,
                        height: "35px",
                        bgcolor: salaryStatus === "Received" ? "#D1FAE5" : "#F3F4F6",
                        color: salaryStatus === "Received" ? "#10B981" : "#6B7280",
                        border: salaryStatus === "Received" ? "2px solid #10B981" : "2px solid #E5E7EB",
                        transition: "all 0.2s",
                        "&:hover": { bgcolor: salaryStatus === "Received" ? "#A7F3D0" : "#E5E7EB" },
                      }}
                    />
                    <Chip
                      label="Not Received"
                      onClick={() => setSalaryStatus("Not Received")}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 600,
                        height: "35px",
                        bgcolor: salaryStatus === "Not Received" ? "#FEE2E2" : "#F3F4F6",
                        color: salaryStatus === "Not Received" ? "#EF4444" : "#6B7280",
                        border: salaryStatus === "Not Received" ? "2px solid #EF4444" : "2px solid #E5E7EB",
                        transition: "all 0.2s",
                        "&:hover": { bgcolor: salaryStatus === "Not Received" ? "#FECACA" : "#E5E7EB" },
                      }}
                    />
                  </Stack>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  disabled={isSubmittingSalary || salaryStatus === initialSalaryStatus}
                  onClick={handleSubmitSalary}
                  sx={{
                    bgcolor: "#4F46E5 !important",
                    color: "#FFFFFF !important",
                    textTransform: "none",
                    height: "45px",
                    borderRadius: "12px",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor: "#3730A3 !important",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(79, 70, 229, 0.35)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#E5E7EB !important",
                      color: "#9CA3AF !important",
                    },
                  }}
                >
                  {isSubmittingSalary ? "Updating..." : "Update Status"}
                </Button>
              </Stack>
            </Paper>
          </Grid>

          {/* Lessons from the field Column */}
          <Grid item xs={12} lg={5}>
            {lessons.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  border: "1px solid #E5E7EB",
                  bgcolor: "white",
                  height: "100%",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        bgcolor: "#E0E7FF",
                        display: "inline-flex",
                      }}
                    >
                      <ArticleIcon sx={{ color: "#4F46E5", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
                        Lessons from the Field
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6B7280" }}>
                        Horizontally scale the skills
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => navigate("/lessons/list")}
                    sx={{
                      color: "#4F46E5",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#EEF2FF" }
                    }}
                    endIcon={<ArrowForwardIcon />}
                  >
                    View All
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  {lessons.slice(0, 3).map((lesson) => (
                    <Card
                      key={lesson.id}
                      onClick={() => navigate(`/lessons/details/${lesson.id}`)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderRadius: "16px",
                        border: "1px solid #F3F4F6",
                        bgcolor: "#F9FAFB",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 10px 25px rgba(79, 70, 229, 0.08)",
                          borderColor: "#4F46E5",
                          bgcolor: "white",
                          "& .chevron-icon": {
                            transform: "translateX(4px)",
                            color: "#4F46E5",
                          }
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: "10px",
                            bgcolor: "white",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                          }}
                        >
                          <ArticleIcon sx={{ color: "#4F46E5", fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#111827",
                              lineHeight: 1.2,
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {lesson.title}
                          </Typography>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#6B7280",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5
                              }}
                            >
                              <Icon sx={{ fontSize: "14px !important" }}>person</Icon>
                              {lesson.user_name}
                            </Typography>
                            {lesson.district_name && lesson.district_name !== "Headquarters" && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#10B981",
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5
                                }}
                              >
                                <Icon sx={{ fontSize: "14px !important" }}>place</Icon>
                                {lesson.district_name}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                        <Box className="chevron-icon" sx={{ alignSelf: 'center', color: "#D1D5DB", transition: "all 0.2s" }}>
                          <Icon fontSize="small">chevron_right</Icon>
                        </Box>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Quick Actions - Floating Cards */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: "#111827" }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2.5}>
            {[
              {
                icon: "event_note",
                title: "Apply Leave",
                color: "#6366F1",
                bgColor: "#EEF2FF",
                route: "/leave-request/add",
              },
              {
                icon: "receipt_long",
                title: "Submit Claim",
                color: "#10B981",
                bgColor: "#D1FAE5",
                route: "/claims/add",
              },
              {
                icon: "support_agent",
                title: "Raise Ticket",
                color: "#EF4444",
                bgColor: "#FEE2E2",
                route: "/ticket-add",
              },
              {
                icon: "post_add",
                title: "Post Lesson",
                color: "#F59E0B",
                bgColor: "#FEF3C7",
                route: "/lessons/add",
              },
            ].map((action, index) => (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  onClick={() => navigate(action.route)}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    borderRadius: "20px",
                    border: "1px solid #E5E7EB",
                    bgcolor: "white",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      boxShadow: `0 20px 60px ${alpha(action.color, 0.2)}`,
                      borderColor: action.color,
                      "& .action-icon": {
                        transform: "scale(1.1) rotate(5deg)",
                      },
                    },
                  }}
                >
                  <Box
                    className="action-icon"
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "18px",
                      bgcolor: action.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      mb: 2,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Icon sx={{ color: action.color, fontSize: 32 }}>{action.icon}</Icon>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
                    {action.title}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </MDBox>
      <Footer />

      <Snackbar
        open={salarySnackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSalarySnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSalarySnackbar}
          severity={salarySnackbar.severity}
          variant="filled"
          sx={{ width: "100%", color: "white !important" }}
        >
          {salarySnackbar.message}
        </Alert>
      </Snackbar>

      <Backdrop open={isLoading || isFetching || isSubmittingSalary} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Lottie options={defaultOptions} height={250} width={250} />
          <Typography variant="h4" color={"green"} mt={-5}>
            Loading
          </Typography>
        </Box>
      </Backdrop>
    </DashboardLayout>
  );
  
}

export default UserDashboard;
